import * as React from "react";
import { clsx } from "clsx";
import { useMutationObserver } from "@web-monorepo/hooks";
import classes from "./DDSScrollLayout.module.css";

// Minimum body space (container height minus header/footer) before collapsing
// to all-scroll mode. If the body would have less than this much room, it's not
// worth having an independent scroll area — just let everything scroll together.
const MIN_BODY_HEIGHT = 300;

export type DDSScrollLayoutProps = {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "className" | "style">;

/**
 * A layout component that provides a fixed header, scrollable body, and fixed footer.
 *
 * When the container is tall enough, the header and footer stay fixed while the body
 * scrolls independently. When the remaining body space (after header and footer) is
 * less than 300px, the entire layout collapses to a single scrollable area.
 *
 * Children should use `data-scroll-role="header"`, `data-scroll-role="body"`, or
 * `data-scroll-role="footer"` attributes (applied automatically by the slot components,
 * or manually for custom integration).
 */
export const DDSScrollLayout = React.forwardRef<HTMLDivElement, DDSScrollLayoutProps>(
  ({ children, className, style, ...props }, forwardedRef) => {
    // The root node lives in state, not a ref, so useMutationObserver picks it
    // up once it resolves — a null target observes nothing.
    const [root, setRoot] = React.useState<HTMLDivElement | null>(null);
    const [collapsed, setCollapsed] = React.useState(false);
    const resizeObserverRef = React.useRef<ResizeObserver | null>(null);

    React.useImperativeHandle(forwardedRef, () => root as HTMLDivElement, [root]);

    const updateCollapsed = React.useCallback(() => {
      if (!root) return;

      const containerHeight = root.clientHeight;
      // Query fresh each time — React may swap out header/footer nodes across renders
      const header = root.querySelector<HTMLElement>('[data-scroll-role="header"]');
      const footer = root.querySelector<HTMLElement>('[data-scroll-role="footer"]');
      const headerHeight = header?.offsetHeight ?? 0;
      const footerHeight = footer?.offsetHeight ?? 0;
      const bodySpace = containerHeight - headerHeight - footerHeight;
      setCollapsed(bodySpace < MIN_BODY_HEIGHT);
    }, [root]);

    // Rebuilds the ResizeObserver targets when children change (e.g. React
    // swaps out header/footer DOM nodes on re-render). Lives outside the effect
    // so useMutationObserver can call it; the ResizeObserver it drives is kept
    // in a ref because the effect owns that observer's lifetime.
    const syncObservedElements = React.useCallback(() => {
      const resizeObserver = resizeObserverRef.current;
      if (!root || !resizeObserver) return;

      resizeObserver.disconnect();
      resizeObserver.observe(root);
      const header = root.querySelector('[data-scroll-role="header"]');
      const footer = root.querySelector('[data-scroll-role="footer"]');
      if (header) resizeObserver.observe(header);
      if (footer) resizeObserver.observe(footer);
      updateCollapsed();
    }, [root, updateCollapsed]);

    React.useEffect(() => {
      if (!root) return;

      if (typeof ResizeObserver === "undefined") {
        setCollapsed(true);
        return;
      }

      const resizeObserver = new ResizeObserver(updateCollapsed);
      resizeObserverRef.current = resizeObserver;

      syncObservedElements();

      return () => {
        resizeObserver.disconnect();
        resizeObserverRef.current = null;
      };
    }, [root, updateCollapsed, syncObservedElements]);

    // Watch for child list changes so we re-attach the ResizeObserver if React
    // swaps out the header/footer nodes.
    // subtree: true is required — when a <form> (or other wrapper with display:contents)
    // wraps the scroll-role children, header/footer are grandchildren of the root, so
    // a flat childList watch would miss their node-swap mutations.
    useMutationObserver(root, syncObservedElements, { childList: true, subtree: true });

    return (
      <div
        ref={setRoot}
        className={clsx(classes.root, collapsed && classes.collapsed, className)}
        style={style}
        {...props}
      >
        {children}
      </div>
    );
  },
);
DDSScrollLayout.displayName = "DDSScrollLayout";

export type DDSScrollLayoutSlotProps = {
  children?: React.ReactNode;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

/**
 * Body-slot props add a required accessible name. The body renders as
 * `role="region"` with `tabIndex={0}` so it's a keyboard-operable landmark
 * (axe rule `scrollable-region-focusable`), which means screen-reader users
 * must be told what region they've focused on.
 */
export type DDSScrollLayoutBodyProps = DDSScrollLayoutSlotProps &
  ({ "aria-label": string } | { "aria-labelledby": string });

export const DDSScrollLayoutHeader = React.forwardRef<HTMLDivElement, DDSScrollLayoutSlotProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div ref={ref} data-scroll-role="header" className={className} {...props}>
        {children}
      </div>
    );
  },
);
DDSScrollLayoutHeader.displayName = "DDSScrollLayoutHeader";

export const DDSScrollLayoutBody = React.forwardRef<HTMLDivElement, DDSScrollLayoutBodyProps>(
  ({ children, className, ...props }, ref) => {
    return (
      // `role="region"` + `tabIndex={0}` makes the scrollable body a keyboard-
      // operable landmark (axe rule `scrollable-region-focusable`). The type
      // enforces an `aria-label` or `aria-labelledby` so the landmark is
      // never nameless to screen readers.
      <div ref={ref} data-scroll-role="body" role="region" tabIndex={0} className={className} {...props}>
        {children}
      </div>
    );
  },
);
DDSScrollLayoutBody.displayName = "DDSScrollLayoutBody";

export const DDSScrollLayoutFooter = React.forwardRef<HTMLDivElement, DDSScrollLayoutSlotProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div ref={ref} data-scroll-role="footer" className={className} {...props}>
        {children}
      </div>
    );
  },
);
DDSScrollLayoutFooter.displayName = "DDSScrollLayoutFooter";
